import { VehicleValuationType } from "@/api-schema-enums";
import { useVehicleValuationCostAdditionSearchQuery } from "@/common/api/vehicle-valuation-cost-addition/use-vehicle-valuation-cost-addition-search.query";
import type { VehicleValuationByUuidResponse } from "@/common/api/vehicle-valuation/use-vehicle-valuation-by-uuid.query";
import { useVehicleValuationConfirmMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-confirm.mutation";
import { canVehicleValuationBeConfirmed } from "@/common/helpers/can-vehicle-valuation-be-confirmed";
import { formatNumberInput } from "@/common/helpers/decimal-number";
import { parseLocalizedNumberInput } from "@/common/helpers/number-format";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCard, FormCardActions, FormCardSection } from "@/components/ui/form-card";
import { InputTextAlign } from "@/components/ui/input";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { RadioGroup, RadioGroupCardItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { VehicleInfoCard } from "@/components/vehicle-info-card/vehicle-info-card";
import { VehicleValuationReimportDisplay } from "@/components/vehicle-valuation/vehicle-valuation-reimport-display";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, InfoIcon } from "lucide-react";
import { useEffect, useEffectEvent, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { VehicleValuationTypeHintPopover } from "../../_components/vehicle-valuation-type-hint-popover";
import {
  getVehicleAccidentStateLabel,
  getVehicleBooleanLabel,
  getVehicleValuationTypeLabel,
  getVehicleValuationTypeOptions,
  getVehicleValuationTypeSummary,
  VEHICLE_VALUATION_CONFIRM_TYPE_VALUES,
} from "../../_helpers/vehicle-valuation-metadata";
import {
  buildInitialFormValues,
  buildVehicleValuationConfirmBody,
  calculateDerivedGrossValues,
  calculateValuesFromMarginGross,
  calculateValuesFromTradeGross,
  DEFAULT_MARGIN_COST_ADDITION_KEY,
  FORM_SET_VALUE_OPTIONS,
  getVehicleValuationConfirmFormSchema,
  resolveAutomaticVehiclePreparationCosts,
  resolveConfirmedAdditionalCostsGross,
  resolveManagedCostAdditionValue,
  shouldShowManagedMarginNotice,
  VEHICLE_PREPARATION_COSTS_COST_ADDITION_KEY,
  type VehicleValuationConfirmFormValues,
} from "../_helpers/vehicle-valuation-confirm-form.helpers";
import { ConfirmedValueField } from "./_components/confirmed-value-field";
import { DetailValue } from "./_components/detail-view";
import { VehicleValuationHistory } from "./_components/vehicle-valuation-history";

type VehicleValuation = VehicleValuationByUuidResponse;

const DECIMAL_NUMBER_INPUT_PROPS = {
  inputMode: "decimal" as const,
  textAlign: "right" as const,
  maxFractionDigits: 4,
};

export function VehicleValuationConfirmFormFields({ valuation }: { valuation: VehicleValuation }) {
  const { t } = useTranslation(["common", "fab", "masterdata"]);
  const [_, navigate] = useLocation();

  const detailPath = `~/fab/list/${valuation.uuid}`;
  const canConfirm = canVehicleValuationBeConfirmed(valuation.status);
  const managedDefaultsInitializationKeyRef = useRef<string | null>(null);
  const hasUserEditedRef = useRef(false);
  const prevValuationTypeRef = useRef<VehicleValuationType | undefined>(undefined);
  const preBuyerValuesRef = useRef<{
    confirmedWarranty: string;
    confirmedVehiclePreparationCosts: string;
    confirmedMarginPercentage: string;
  } | null>(null);

  const { mutateAsync, isPending } = useVehicleValuationConfirmMutation();
  const managedCostAdditionsQuery = useVehicleValuationCostAdditionSearchQuery({
    enabled: canConfirm,
    options: {
      body: { key: [DEFAULT_MARGIN_COST_ADDITION_KEY, VEHICLE_PREPARATION_COSTS_COST_ADDITION_KEY] },
      parameters: { pageSize: -1 },
    },
  });

  const formSchema = useMemo(() => getVehicleValuationConfirmFormSchema(t), [t]);
  const managedDefaultMarginPercentage = useMemo(() => {
    return resolveManagedCostAdditionValue(managedCostAdditionsQuery.data?.data, DEFAULT_MARGIN_COST_ADDITION_KEY);
  }, [managedCostAdditionsQuery.data?.data]);
  const managedVehiclePreparationCosts = useMemo(() => {
    return resolveManagedCostAdditionValue(
      managedCostAdditionsQuery.data?.data,
      VEHICLE_PREPARATION_COSTS_COST_ADDITION_KEY,
    );
  }, [managedCostAdditionsQuery.data?.data]);
  const effectiveVehiclePreparationCosts = resolveAutomaticVehiclePreparationCosts(
    valuation,
    managedVehiclePreparationCosts,
  );
  const managedInitialFormValues = useMemo(
    () =>
      buildInitialFormValues(valuation, {
        managedDefaultMarginPercentage,
        managedVehiclePreparationCosts,
      }),
    [managedDefaultMarginPercentage, managedVehiclePreparationCosts, valuation],
  );
  const form = useForm<VehicleValuationConfirmFormValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: managedInitialFormValues,
  });
  const confirmedMarginPercentageValue = useWatch({
    control: form.control,
    name: "confirmedMarginPercentage",
  });
  const valuationTypeValue = useWatch({
    control: form.control,
    name: "valuationType",
  });
  const isLeasingBuyoutType = valuationTypeValue === VehicleValuationType.LeasingBuyout;
  const isWholesaleBuyerType = valuationTypeValue === VehicleValuationType.WholesaleBuyer;
  const valuationTypeOptions = useMemo(
    () => getVehicleValuationTypeOptions(t, VEHICLE_VALUATION_CONFIRM_TYPE_VALUES),
    [t],
  );
  const shouldShowManagedMarginTooltip = useMemo(
    () =>
      !isWholesaleBuyerType &&
      shouldShowManagedMarginNotice(confirmedMarginPercentageValue ?? "", managedDefaultMarginPercentage),
    [isWholesaleBuyerType, confirmedMarginPercentageValue, managedDefaultMarginPercentage],
  );

  function markUserEdited(): void {
    hasUserEditedRef.current = true;
  }

  function syncFromSalesAndMarginPercentage(
    salesGrossValue: string,
    marginPercentageValue: string,
    confirmedWarrantyValue = form.getValues("confirmedWarranty"),
    confirmedVehiclePreparationCostsValue = form.getValues("confirmedVehiclePreparationCosts"),
  ): void {
    const salesGross = parseLocalizedNumberInput(salesGrossValue, { allowNegative: false });
    const marginPercentage = parseLocalizedNumberInput(marginPercentageValue, { allowNegative: true });
    const additionalCostsGross = resolveConfirmedAdditionalCostsGross(
      confirmedWarrantyValue,
      confirmedVehiclePreparationCostsValue,
    );

    if (!Number.isFinite(salesGross) || !Number.isFinite(marginPercentage) || additionalCostsGross == null) {
      return;
    }

    const { confirmedMarginGross, confirmedTradeGross } = calculateDerivedGrossValues(
      salesGross,
      marginPercentage,
      additionalCostsGross,
    );

    form.setValue("confirmedMarginGross", formatNumberInput(confirmedMarginGross), FORM_SET_VALUE_OPTIONS);
    form.setValue("confirmedTradeGross", formatNumberInput(confirmedTradeGross), FORM_SET_VALUE_OPTIONS);
  }

  function syncFromSalesAndTradeGross(
    salesGrossValue: string,
    tradeGrossValue: string,
    confirmedWarrantyValue = form.getValues("confirmedWarranty"),
    confirmedVehiclePreparationCostsValue = form.getValues("confirmedVehiclePreparationCosts"),
  ): void {
    const salesGross = parseLocalizedNumberInput(salesGrossValue, { allowNegative: false });
    const tradeGross = parseLocalizedNumberInput(tradeGrossValue, { allowNegative: false });
    const additionalCostsGross = resolveConfirmedAdditionalCostsGross(
      confirmedWarrantyValue,
      confirmedVehiclePreparationCostsValue,
    );

    if (!Number.isFinite(salesGross) || !Number.isFinite(tradeGross) || additionalCostsGross == null) {
      return;
    }

    const { confirmedMarginGross, confirmedMarginPercentage } = calculateValuesFromTradeGross(
      salesGross,
      tradeGross,
      additionalCostsGross,
    );

    form.setValue("confirmedMarginGross", formatNumberInput(confirmedMarginGross), FORM_SET_VALUE_OPTIONS);
    form.setValue("confirmedMarginPercentage", formatNumberInput(confirmedMarginPercentage), FORM_SET_VALUE_OPTIONS);
  }

  function syncFromSalesAndMarginGross(
    salesGrossValue: string,
    marginGrossValue: string,
    confirmedWarrantyValue = form.getValues("confirmedWarranty"),
    confirmedVehiclePreparationCostsValue = form.getValues("confirmedVehiclePreparationCosts"),
  ): void {
    const salesGross = parseLocalizedNumberInput(salesGrossValue, { allowNegative: false });
    const marginGross = parseLocalizedNumberInput(marginGrossValue, { allowNegative: true });
    const additionalCostsGross = resolveConfirmedAdditionalCostsGross(
      confirmedWarrantyValue,
      confirmedVehiclePreparationCostsValue,
    );

    if (!Number.isFinite(salesGross) || !Number.isFinite(marginGross) || additionalCostsGross == null) {
      return;
    }

    const { confirmedTradeGross, confirmedMarginPercentage } = calculateValuesFromMarginGross(
      salesGross,
      marginGross,
      additionalCostsGross,
    );

    form.setValue("confirmedTradeGross", formatNumberInput(confirmedTradeGross), FORM_SET_VALUE_OPTIONS);
    form.setValue("confirmedMarginPercentage", formatNumberInput(confirmedMarginPercentage), FORM_SET_VALUE_OPTIONS);
  }

  const resetForValuation = useEffectEvent(() => {
    hasUserEditedRef.current = false;
    managedDefaultsInitializationKeyRef.current = null;
    prevValuationTypeRef.current = undefined;
    preBuyerValuesRef.current = null;
    form.reset(managedInitialFormValues);
  });

  // Only reset when navigating to a different valuation, not on background refetches
  useEffect(() => {
    resetForValuation();
  }, [valuation.uuid]);

  useEffect(() => {
    const initializationKey = `${valuation.uuid}:${managedDefaultMarginPercentage ?? "fallback"}:${managedVehiclePreparationCosts ?? "fallback"}`;

    if (
      !managedCostAdditionsQuery.isFetched ||
      hasUserEditedRef.current ||
      managedDefaultsInitializationKeyRef.current === initializationKey
    ) {
      return;
    }

    form.reset(managedInitialFormValues);
    managedDefaultsInitializationKeyRef.current = initializationKey;
  }, [
    form,
    managedInitialFormValues,
    managedCostAdditionsQuery.isFetched,
    managedDefaultMarginPercentage,
    managedVehiclePreparationCosts,
    valuation.uuid,
  ]);

  const onValuationTypeChanged = useEffectEvent((newType: VehicleValuationType) => {
    markUserEdited();

    if (newType === VehicleValuationType.WholesaleBuyer) {
      preBuyerValuesRef.current = {
        confirmedWarranty: form.getValues("confirmedWarranty") ?? "",
        confirmedVehiclePreparationCosts: form.getValues("confirmedVehiclePreparationCosts") ?? "",
        confirmedMarginPercentage: form.getValues("confirmedMarginPercentage") ?? "",
      };
      form.setValue("confirmedWarranty", "0", FORM_SET_VALUE_OPTIONS);
      form.setValue("confirmedVehiclePreparationCosts", "0", FORM_SET_VALUE_OPTIONS);
      form.setValue("confirmedMarginPercentage", "0", FORM_SET_VALUE_OPTIONS);
      syncFromSalesAndMarginPercentage(form.getValues("confirmedSalesGross") ?? "", "0", "0", "0");
    } else {
      const restoredWarranty =
        preBuyerValuesRef.current?.confirmedWarranty ?? managedInitialFormValues.confirmedWarranty ?? "";
      const restoredCosts =
        preBuyerValuesRef.current?.confirmedVehiclePreparationCosts ??
        managedInitialFormValues.confirmedVehiclePreparationCosts ??
        "";
      const restoredMarginPercentage =
        preBuyerValuesRef.current?.confirmedMarginPercentage ??
        managedInitialFormValues.confirmedMarginPercentage ??
        "";
      preBuyerValuesRef.current = null;
      form.setValue("confirmedWarranty", restoredWarranty, FORM_SET_VALUE_OPTIONS);
      form.setValue("confirmedVehiclePreparationCosts", restoredCosts, FORM_SET_VALUE_OPTIONS);
      form.setValue("confirmedMarginPercentage", restoredMarginPercentage, FORM_SET_VALUE_OPTIONS);
      syncFromSalesAndMarginPercentage(
        form.getValues("confirmedSalesGross") ?? "",
        restoredMarginPercentage,
        restoredWarranty,
        restoredCosts,
      );
    }
  });

  useEffect(() => {
    const prev = prevValuationTypeRef.current;
    prevValuationTypeRef.current = valuationTypeValue;

    if (prev === undefined || prev === valuationTypeValue) return;

    onValuationTypeChanged(valuationTypeValue);
  }, [valuationTypeValue]);

  const onSubmit = async (values: VehicleValuationConfirmFormValues) => {
    await toastMutationStatus({
      mutationFn: () => mutateAsync(buildVehicleValuationConfirmBody(valuation.uuid, values)),
      successMessage: t("masterdata:VehicleValuationConfirmed"),
      errorMessage: t("masterdata:ErrorConfirmingVehicleValuation"),
    });

    navigate(detailPath);
  };

  return (
    <div className="flex flex-col gap-6">
      {valuation.vehicle ? <VehicleInfoCard vehicle={valuation.vehicle} /> : null}

      {valuation.vehicle?.uuid ? <VehicleValuationHistory vehicleUuid={valuation.vehicle.uuid} /> : null}

      {!canConfirm ? (
        <>
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("masterdata:ConfirmVehicleValuationUnavailable")}</AlertTitle>
            <AlertDescription>{t("masterdata:VehicleValuationCanOnlyBeConfirmedWhileInProgress")}</AlertDescription>
          </Alert>

          <div className="flex justify-end">
            <Button
              type="button"
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
              onClick={() => navigate(detailPath)}
            >
              {t("masterdata:BackToVehicleValuationDetails")}
            </Button>
          </div>
        </>
      ) : (
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormCard
              title={t("masterdata:ConfirmVehicleValuation")}
              description={t("masterdata:ConfirmVehicleValuationDescription")}
            >
              <div className="space-y-6">
                <FormCardSection title={t("fab:VehicleValuationSetupSectionTitle")} contentClassName="grid gap-3">
                  {isLeasingBuyoutType ? (
                    <DetailValue
                      label={
                        <span className="inline-flex items-center gap-2">
                          {t("fab:VehicleValuationType")}
                          <VehicleValuationTypeHintPopover />
                        </span>
                      }
                      value={getVehicleValuationTypeLabel(valuationTypeValue, t)}
                    />
                  ) : (
                    <FormField
                      control={form.control}
                      name="valuationType"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="mb-0">{t("fab:VehicleValuationType")}</FormLabel>
                            <VehicleValuationTypeHintPopover />
                          </div>
                          <FormControl>
                            <RadioGroup
                              value={field.value}
                              onValueChange={field.onChange}
                              className="grid gap-3 md:grid-cols-2"
                            >
                              {valuationTypeOptions.map((option) => {
                                const isSelected = field.value === option.value;
                                const optionSummary = getVehicleValuationTypeSummary(option.value, t);
                                const optionId = `${field.name}-${option.value}`;

                                return (
                                  <RadioGroupCardItem
                                    key={option.value}
                                    id={optionId}
                                    value={option.value}
                                    selected={isSelected}
                                    label={option.label}
                                    description={optionSummary}
                                  />
                                );
                              })}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <DetailValue
                      label={t("fab:EarlyLeaseTermination")}
                      value={getVehicleBooleanLabel(valuation.earlyLeaseTermination, t)}
                    />
                    <DetailValue
                      label={t("fab:EarlyLeaseTerminationAmount")}
                      value={automaticCellRenderer(valuation.earlyLeaseTerminationAmount, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                    />
                  </div>
                </FormCardSection>

                <FormCardSection
                  title={t("masterdata:VehicleValuationResult")}
                  contentClassName="grid gap-4 md:grid-cols-2"
                >
                  <DetailValue
                    label={t("masterdata:SalesGross")}
                    value={automaticCellRenderer(valuation.salesGross, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailValue
                    label={t("masterdata:TradeGross")}
                    value={automaticCellRenderer(valuation.tradeGross, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailValue
                    label={t("masterdata:MarginPercentage")}
                    value={automaticCellRenderer(valuation.marginPercentage, {
                      numberFormat: "percentage",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailValue
                    label={t("masterdata:MarginGross")}
                    value={automaticCellRenderer(valuation.marginGross, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailValue
                    label={t("masterdata:Warranty")}
                    value={automaticCellRenderer(valuation.warranty, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailValue
                    label={t("masterdata:VehiclePreparationCosts")}
                    value={automaticCellRenderer(effectiveVehiclePreparationCosts, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                </FormCardSection>

                <FormCardSection title={t("masterdata:VehicleDetails")} contentClassName="grid gap-4 md:grid-cols-3">
                  <DetailValue
                    label={t("masterdata:AccidentState")}
                    value={getVehicleAccidentStateLabel(valuation.accidentState, t)}
                  />
                  <DetailValue
                    label={t("masterdata:VatDeductible")}
                    value={getVehicleBooleanLabel(valuation.vatDeductible, t)}
                  />
                  <DetailValue
                    label={t("masterdata:Reimport")}
                    value={<VehicleValuationReimportDisplay reimport={valuation.reimport} />}
                  />
                </FormCardSection>

                {isLeasingBuyoutType ? (
                  <FormCardSection
                    title={t("fab:VehicleValuationLeaseSectionTitle")}
                    contentClassName="grid gap-x-4 gap-y-4 md:grid-cols-2"
                  >
                    <div className="md:col-span-2 md:max-w-56">
                      <FormField
                        control={form.control}
                        name="leaseBuyoutAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>{t("fab:LeaseBuyoutAmount")}</FormLabel>
                            <FormControl>
                              <LocalizedNumberInput
                                min="0"
                                inputMode="decimal"
                                maxFractionDigits={4}
                                name={field.name}
                                ref={field.ref}
                                value={field.value ?? ""}
                                onBlur={field.onBlur}
                                textAlign={InputTextAlign.Right}
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  markUserEdited();
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </FormCardSection>
                ) : null}

                {!isLeasingBuyoutType ? (
                  <FormCardSection
                    title={t("fab:VehicleValuationConfirmationValuesSectionTitle")}
                    contentClassName="grid gap-4 md:grid-cols-2"
                  >
                    <div className="md:col-span-2">
                      <ConfirmedValueField
                        control={form.control}
                        name="confirmedSalesGross"
                        label={t("masterdata:ConfirmedSalesGross")}
                        inputProps={{
                          ...DECIMAL_NUMBER_INPUT_PROPS,
                          min: "0",
                          prefix: "€",
                          textAlign: InputTextAlign.Right,
                        }}
                        onChangeValue={(value) => {
                          markUserEdited();
                          syncFromSalesAndMarginPercentage(value, form.getValues("confirmedMarginPercentage") ?? "");
                        }}
                      />
                    </div>

                    <ConfirmedValueField
                      control={form.control}
                      name="confirmedMarginPercentage"
                      label={t("masterdata:ConfirmedMarginPercentage")}
                      labelAccessory={
                        shouldShowManagedMarginTooltip ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={t("masterdata:VehicleValuationManagedMarginNoticeTitle")}
                                className="text-muted-foreground hover:text-foreground inline-flex size-4 items-center justify-center rounded-sm transition-colors"
                              >
                                <InfoIcon className="size-3.5" />
                              </button>
                            </TooltipTrigger>

                            <TooltipContent className="max-w-80">
                              <p className="font-medium">{t("masterdata:VehicleValuationManagedMarginNoticeTitle")}</p>
                              <p className="mt-1">{t("masterdata:VehicleValuationManagedMarginNoticeDescription")}</p>
                              <p className="mt-2 font-medium">
                                {t("fab:DefaultMargin")}:{" "}
                                {automaticCellRenderer(managedDefaultMarginPercentage, {
                                  numberFormat: "percentage",
                                  numberDecimals: 2,
                                })}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : null
                      }
                      inputProps={{
                        ...DECIMAL_NUMBER_INPUT_PROPS,
                        min: "0",
                        max: "100",
                        prefix: "%",
                        disabled: isWholesaleBuyerType,
                        textAlign: InputTextAlign.Right,
                      }}
                      onChangeValue={(value) => {
                        markUserEdited();
                        syncFromSalesAndMarginPercentage(form.getValues("confirmedSalesGross") ?? "", value);
                      }}
                    />

                    <ConfirmedValueField
                      control={form.control}
                      name="confirmedMarginGross"
                      label={t("masterdata:ConfirmedMarginGross")}
                      inputProps={{
                        ...DECIMAL_NUMBER_INPUT_PROPS,
                        prefix: "€",
                        disabled: isWholesaleBuyerType,
                        textAlign: InputTextAlign.Right,
                      }}
                      onChangeValue={(value) => {
                        markUserEdited();
                        syncFromSalesAndMarginGross(form.getValues("confirmedSalesGross") ?? "", value);
                      }}
                    />

                    <ConfirmedValueField
                      control={form.control}
                      name="confirmedWarranty"
                      label={t("masterdata:ConfirmedWarranty")}
                      inputProps={{
                        ...DECIMAL_NUMBER_INPUT_PROPS,
                        min: "0",
                        prefix: "€",
                        disabled: isWholesaleBuyerType,
                        textAlign: InputTextAlign.Right,
                      }}
                      onChangeValue={(value) => {
                        markUserEdited();
                        syncFromSalesAndMarginGross(
                          form.getValues("confirmedSalesGross") ?? "",
                          form.getValues("confirmedMarginGross") ?? "",
                          value,
                          form.getValues("confirmedVehiclePreparationCosts") ?? "",
                        );
                      }}
                    />

                    <ConfirmedValueField
                      control={form.control}
                      name="confirmedVehiclePreparationCosts"
                      label={t("masterdata:ConfirmedVehiclePreparationCosts")}
                      inputProps={{
                        ...DECIMAL_NUMBER_INPUT_PROPS,
                        min: "0",
                        prefix: "€",
                        disabled: isWholesaleBuyerType,
                        textAlign: InputTextAlign.Right,
                      }}
                      onChangeValue={(value) => {
                        markUserEdited();
                        syncFromSalesAndMarginGross(
                          form.getValues("confirmedSalesGross") ?? "",
                          form.getValues("confirmedMarginGross") ?? "",
                          form.getValues("confirmedWarranty") ?? "",
                          value,
                        );
                      }}
                    />

                    <div className="md:col-span-2">
                      <ConfirmedValueField
                        control={form.control}
                        name="confirmedTradeGross"
                        label={t("masterdata:ConfirmedTradeGross")}
                        inputProps={{
                          ...DECIMAL_NUMBER_INPUT_PROPS,
                          min: "0",
                          prefix: "€",
                          disabled: isWholesaleBuyerType,
                          textAlign: InputTextAlign.Right,
                        }}
                        onChangeValue={(value) => {
                          markUserEdited();
                          syncFromSalesAndTradeGross(form.getValues("confirmedSalesGross") ?? "", value);
                        }}
                      />
                    </div>
                  </FormCardSection>
                ) : null}

                <FormCardSection title={t("masterdata:VehicleValuationNote")} contentClassName="grid gap-3">
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("masterdata:VehicleValuationNote")}</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value ?? ""}
                            className="min-h-28"
                            rows={4}
                            onChange={(event) => {
                              field.onChange(event);
                              markUserEdited();
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormCardSection>
              </div>
            </FormCard>

            <FormCardActions
              cancelLabel={t("common:Cancel")}
              onCancel={() => navigate(detailPath)}
              isPending={isPending}
              submitDisabled={isPending || !form.formState.isValid}
              submitLabel={t("common:Confirm")}
            />
          </form>
        </Form>
      )}
    </div>
  );
}
